import { t } from "i18next";
import MarkdownView from "../../../../components/markdown/MarkdownView";
import { SafetyDiagStepView } from "../../../../types/safety-diag/safetyDiagReport";
import SafetyDiagMediaImage, { safetyDiagMediaPath } from "./SafetyDiagMediaImage";

/** Localized Yes/No, colored green for YES and red for NO. */
function AnswerBadge({ answer }: { answer: "YES" | "NO" }) {
  const isYes = answer === "YES";
  return (
    <span
      className={`font-semibold px-2 py-0.5 rounded text-xs inline-flex items-center ${
        isYes
          ? "bg-green-100 text-green-800"
          : "bg-red-100 text-red-800"
      }`}
    >
      {isYes ? t("diagSecu:report:answerYES") : t("diagSecu:report:answerNO")}
    </span>
  );
}

/**
 * Ordered question/answer timeline of a safety-diag realization: each step shows its
 * (localized) label, the answer(s), and any captured media. For a multi-responder QUESTION
 * it shows each responder's own answer (technician / client) plus the routing decision
 * attributed to the "répondant pilote"; single-responder / legacy steps show one answer.
 */
export default function SafetyDiagStepTimeline({
  steps,
  workOrderId,
}: {
  steps: SafetyDiagStepView[];
  workOrderId: string;
}) {
  if (!steps.length) {
    return null;
  }

  return (
    <ol className="flex flex-col gap-3">
      {steps.map((step, index) => (
        <li
          key={`${step.stepId}-${index}`}
          className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
        >
          {step.stepType && (
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-600 block mb-1">
              {t(`diagSecu:report:stepType:${step.stepType}`, { defaultValue: step.stepType })}
            </span>
          )}
          <MarkdownView className="text-base font-semibold text-gray-900">{step.label}</MarkdownView>

          {step.responderAnswers.length > 0 ? (
            <div className="mt-2 space-y-1 text-sm">
              {step.responderAnswers.map((ra) => (
                <p key={ra.responder} className="flex items-center gap-1.5">
                  <span className="text-gray-800 font-medium">
                    {t(`diagSecu:report:responder:${ra.responder}`)}:{" "}
                  </span>
                  <AnswerBadge answer={ra.answer} />
                </p>
              ))}
              {step.answer && (
                <p className="flex items-center gap-1.5 pt-0.5">
                  <span className="text-gray-800 font-medium">
                    {t("diagSecu:report:routingDecision")}:{" "}
                  </span>
                  <AnswerBadge answer={step.answer} />
                  {step.routingResponder && (
                    <span className="text-gray-600 text-xs">
                      {" "}
                      ({t("diagSecu:report:routingResponder")}:{" "}
                      {t(`diagSecu:report:responder:${step.routingResponder}`)})
                    </span>
                  )}
                </p>
              )}
            </div>
          ) : (
            step.answer && (
              <p className="mt-2 flex items-center gap-1.5 text-sm">
                <span className="text-gray-800 font-medium">{t("diagSecu:report:answer")}: </span>
                <AnswerBadge answer={step.answer} />
              </p>
            )
          )}

          {step.captures.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {step.captures.map((cap) =>
                cap.captureType === "LINK" ? (
                  <a
                    key={cap.captureId}
                    href={cap.mediaUrl ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 underline break-all"
                  >
                    {cap.mediaUrl}
                  </a>
                ) : (
                  <SafetyDiagMediaImage
                    key={cap.captureId}
                    downloadPath={safetyDiagMediaPath(workOrderId, cap.captureId)}
                    alt={cap.captureType}
                    modalTitle={step.label}
                    previewable
                    className="h-24 w-24 rounded border border-gray-200 object-cover"
                  />
                )
              )}
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}
